/* eslint-disable */

import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

function isPermissionError(err) {
  const code = err?.code || "";
  const msg = (err?.message || "").toLowerCase();
  return (
    code === "permission-denied" ||
    msg.includes("missing or insufficient permissions") ||
    msg.includes("permission") ||
    msg.includes("insufficient")
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function firstNameFromUser(user) {
  const name = user?.displayName || user?.email?.split("@")?.[0] || "there";
  return String(name).split(" ")[0];
}

export default function HomeDashboard() {
  const navigate = useNavigate();
  const { user, role, profile, loading, logout, signInWithGoogle } = useAuth();
  const schoolId = profile?.schoolId || null;

  const [summary, setSummary] = useState({
    students: 0,
    markedToday: 0,
    requiresFollowUp: 0,
    attendanceRate: 0,
    absentCount: 0,
    lateCount: 0,
  });

  const [state, setState] = useState({
    loading: true,
    error: "",
    denied: false,
  });

  const canView = useMemo(
    () => role === "admin" || role === "staff" || role === "teacher",
    [role]
  );

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!user) {
        if (mounted) setState({ loading: false, error: "", denied: false });
        return;
      }

      if (!canView) {
        if (mounted) setState({ loading: false, error: "", denied: false });
        return;
      }

      if (!schoolId) {
        if (mounted) {
          setState({
            loading: false,
            error: "No school linked to this account.",
            denied: false,
          });
        }
        return;
      }

      if (mounted) setState({ loading: true, error: "", denied: false });

      try {
        const studentsSnap = await getDocs(
          query(collection(db, "schools", schoolId, "students"), limit(2000))
        );
        const studentsCount = studentsSnap.size;

        let markedToday = 0;
        let requiresFollowUp = 0;
        let absentCount = 0;
        let lateCount = 0;

        try {
          const attendanceSnap = await getDocs(
            query(
              collection(db, "schools", schoolId, "attendance"),
              orderBy("timestamp", "desc"),
              limit(300)
            )
          );

          attendanceSnap.forEach((d) => {
            const a = d.data() || {};
            const status = String(a.status || "").trim().toLowerCase();

            if (status) markedToday += 1;

            if (status === "absent") {
              absentCount += 1;
              requiresFollowUp += 1;
            }

            if (status === "late") {
              lateCount += 1;
              requiresFollowUp += 1;
            }
          });
        } catch (e) {
          if (!isPermissionError(e)) throw e;
        }

        const attendanceRate =
          studentsCount > 0
            ? Math.max(
                0,
                Math.round(((markedToday - absentCount) / studentsCount) * 100)
              )
            : 0;

        if (!mounted) return;

        setSummary({
          students: studentsCount,
          markedToday,
          requiresFollowUp,
          attendanceRate,
          absentCount,
          lateCount,
        });

        setState({ loading: false, error: "", denied: false });
      } catch (e) {
        if (!mounted) return;

        if (isPermissionError(e)) {
          setState({ loading: false, error: "", denied: true });
        } else {
          setState({
            loading: false,
            error: "Failed to load dashboard data.",
            denied: false,
          });
        }
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [user, canView, schoolId]);

  if (loading) {
    return (
      <div style={centerState}>
        <div style={centerCard}>
          <h2 style={{ margin: 0 }}>Loading dashboard…</h2>
          <p style={mutedText}>Preparing your PresentPlus workspace.</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={centerState}>
        <div style={centerCard}>
          <h2 style={{ margin: 0 }}>Please sign in</h2>
          <p style={mutedText}>You need an account to access PresentPlus.</p>
          <div style={{ marginTop: 16 }}>
            <button style={btnPrimary} onClick={signInWithGoogle}>
              Sign in with Google
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div style={centerState}>
        <div style={centerCard}>
          <h2 style={{ margin: 0 }}>Access restricted</h2>
          <p style={mutedText}>
            Your role (<b>{role || "none"}</b>) cannot view the Attendance
            Command Centre.
          </p>
          <div style={{ marginTop: 16 }}>
            <button style={btnGhost} onClick={logout}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (state.denied) {
    return (
      <div style={centerState}>
        <div style={centerCard}>
          <h2 style={{ margin: 0 }}>Access restricted</h2>
          <p style={mutedText}>
            You are signed in as <b>{role}</b>, but Firestore rules blocked
            dashboard reads.
          </p>
          <div style={{ marginTop: 16 }}>
            <button style={btnGhost} onClick={logout}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  const greeting = getGreeting();
  const firstName = firstNameFromUser(user);

  return (
    <div style={pageWrap}>
      <section style={heroCard}>
        <div style={{ minWidth: 0 }}>
          <div style={eyebrow}>Attendance workspace</div>
          <h1 style={heroTitle}>
            {greeting}, {firstName}
          </h1>
          <p style={heroSubtitle}>
            Monitor attendance, identify follow-up needs, and keep today’s
            school activity in view.
          </p>

          <div style={heroPills}>
            <span style={pillBlue}>{String(role || "user").toUpperCase()}</span>
            <span style={pillSoft}>School Roll {summary.students}</span>
            <span style={pillSoft}>Follow-up {summary.requiresFollowUp}</span>
          </div>
        </div>

        <div style={heroActions}>
          <button style={btnPrimary} onClick={() => navigate("/daily")}>
            Open Daily Register
          </button>
          {role === "admin" ? (
            <button style={btnSoft} onClick={() => navigate("/admin-manager")}>
              Admin Manager
            </button>
          ) : null}
        </div>
      </section>

      {state.error ? <div style={errorBanner}>{state.error}</div> : null}

      <section style={statsGrid}>
        <MetricCard
          label="School Roll"
          value={summary.students}
          note="Total enrolled students"
          accent="linear-gradient(90deg,#3b82f6,#1d4ed8)"
          glow="rgba(59,130,246,0.15)"
        />

        <MetricCard
          label="Marked Today"
          value={summary.markedToday}
          note="Recent attendance marks recorded"
          accent="linear-gradient(90deg,#22c55e,#16a34a)"
          glow="rgba(34,197,94,0.14)"
        />

        <MetricCard
          label="Needs Follow-up"
          value={summary.requiresFollowUp}
          note="Late and absent cases"
          accent="linear-gradient(90deg,#f59e0b,#d97706)"
          glow="rgba(245,158,11,0.14)"
        />

        <MetricCard
          label="Attendance Rate"
          value={`${summary.attendanceRate}%`}
          note="Whole school estimate"
          accent="linear-gradient(90deg,#8b5cf6,#7c3aed)"
          glow="rgba(139,92,246,0.14)"
        />
      </section>

      <section style={contentGrid}>
        <div style={glassPanelLarge}>
          <PanelHeader
            title="Today’s Attendance Overview"
            subtitle="A quick operational view of today’s register activity."
          />

          <div style={miniStatsGrid}>
            <MiniStat
              title="Students on roll"
              value={summary.students}
              note="School population"
            />
            <MiniStat
              title="Registers marked"
              value={summary.markedToday}
              note="Entries captured"
            />
            <MiniStat
              title="Absent"
              value={summary.absentCount}
              note="Requires review"
            />
            <MiniStat
              title="Late"
              value={summary.lateCount}
              note="Monitor punctuality"
            />
          </div>

          <div style={divider} />

          <div style={summaryRows}>
            <SummaryRow
              label="School roll coverage"
              value={
                summary.students > 0
                  ? `${Math.min(
                      100,
                      Math.round((summary.markedToday / summary.students) * 100)
                    )}%`
                  : "0%"
              }
            />
            <SummaryRow
              label="Follow-up workload"
              value={`${summary.requiresFollowUp} cases`}
            />
            <SummaryRow
              label="Operational status"
              valueNode={
                <span style={statusPill}>
                  {summary.requiresFollowUp > 0 ? "Attention needed" : "Stable"}
                </span>
              }
            />
          </div>
        </div>

        <div style={stackCol}>
          <div style={glassPanel}>
            <PanelHeader
              title="Action Queue"
              subtitle="Suggested next steps for the team."
            />

            <ActionItem
              title="Open Daily Register"
              text="Continue attendance capture for today’s classes."
            />
            <ActionItem
              title="Review absent pupils"
              text={`${summary.absentCount} absent records currently need review.`}
            />
            <ActionItem
              title="Check late arrivals"
              text={`${summary.lateCount} late entries may require follow-up.`}
            />
          </div>

          <div style={glassPanel}>
            <PanelHeader
              title="Quick Snapshot"
              subtitle="Clean summary for leadership and admin."
            />

            <SummaryRow
              label="Role"
              value={String(role || "—").toUpperCase()}
            />
            <SummaryRow label="User" value={user?.email || "—"} />
            <SummaryRow
              label="School"
              value={profile?.schoolName || schoolId || "—"}
            />
            <SummaryRow
              label="Follow-up"
              value={`${summary.requiresFollowUp} open`}
            />
            <SummaryRow
              label="Attendance"
              value={`${summary.attendanceRate}%`}
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function PanelHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={panelTitle}>{title}</div>
      <div style={panelSubtitle}>{subtitle}</div>
    </div>
  );
}

function MetricCard({ label, value, note, accent, glow }) {
  return (
    <div style={metricCard}>
      <div
        style={{
          position: "absolute",
          inset: "0 auto auto 0",
          width: "100%",
          height: 4,
          background: accent,
        }}
      />

      <div
        style={{
          position: "absolute",
          right: -20,
          top: -20,
          width: 120,
          height: 120,
          borderRadius: 999,
          background: glow,
          filter: "blur(40px)",
          opacity: 0.9,
          pointerEvents: "none",
        }}
      />

      <div style={metricLabel}>{label}</div>
      <div style={metricValue}>{value}</div>
      <div style={metricNote}>{note}</div>
    </div>
  );
}

function MiniStat({ title, value, note }) {
  return (
    <div style={miniStatCard}>
      <div style={miniStatTitle}>{title}</div>
      <div style={miniStatValue}>{value}</div>
      <div style={miniStatNote}>{note}</div>
    </div>
  );
}

function ActionItem({ title, text }) {
  return (
    <div style={actionItem}>
      <div style={actionDot} />
      <div>
        <div style={actionTitle}>{title}</div>
        <div style={actionText}>{text}</div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, valueNode }) {
  return (
    <div style={summaryRow}>
      <span style={summaryLabel}>{label}</span>
      {valueNode ? valueNode : <span style={summaryValue}>{value}</span>}
    </div>
  );
}

const pageWrap = {
  display: "flex",
  flexDirection: "column",
  gap: 18,
};

const heroCard = {
  position: "relative",
  overflow: "hidden",
  borderRadius: 32,
  border: "1px solid rgba(255,255,255,0.75)",
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.75), rgba(255,255,255,0.55))",
  backdropFilter: "blur(18px)",
  boxShadow: "0 20px 70px rgba(15,23,42,0.08)",
  padding: 26,
  display: "flex",
  justifyContent: "space-between",
  gap: 20,
  flexWrap: "wrap",
};

const eyebrow = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: "#94a3b8",
};

const heroTitle = {
  margin: "8px 0 0",
  fontSize: 28,
  lineHeight: 1.08,
  fontWeight: 800,
  color: "#0f172a",
};

const heroSubtitle = {
  marginTop: 10,
  maxWidth: 680,
  fontSize: 14,
  lineHeight: 1.65,
  color: "#64748b",
};

const heroPills = {
  marginTop: 14,
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
};

const pillBlue = {
  padding: "8px 12px",
  borderRadius: 999,
  background: "linear-gradient(180deg,#eff6ff 0%,#dbeafe 100%)",
  color: "#1d4ed8",
  border: "1px solid rgba(59,130,246,0.12)",
  fontSize: 12,
  fontWeight: 800,
};

const pillSoft = {
  padding: "8px 12px",
  borderRadius: 999,
  background: "rgba(248,250,252,0.92)",
  color: "#475569",
  border: "1px solid rgba(226,232,240,0.95)",
  fontSize: 12,
  fontWeight: 700,
};

const heroActions = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  alignItems: "center",
};

const statsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 14,
};

const metricCard = {
  position: "relative",
  overflow: "hidden",
  borderRadius: 26,
  border: "1px solid rgba(255,255,255,0.7)",
  background: "rgba(255,255,255,0.65)",
  backdropFilter: "blur(18px)",
  boxShadow:
    "0 12px 40px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.7)",
  padding: 20,
  minHeight: 130,
  transition: "all 0.25s ease",
};

const metricLabel = {
  position: "relative",
  zIndex: 2,
  fontSize: 13,
  fontWeight: 700,
  color: "#64748b",
};

const metricValue = {
  position: "relative",
  zIndex: 2,
  marginTop: 16,
  fontSize: 42,
  lineHeight: 1,
  fontWeight: 800,
  color: "#0f172a",
};

const metricNote = {
  position: "relative",
  zIndex: 2,
  marginTop: 12,
  fontSize: 13,
  lineHeight: 1.5,
  color: "#64748b",
};

const contentGrid = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.6fr) minmax(320px, 0.85fr)",
  gap: 18,
};

const glassPanelLarge = {
  borderRadius: 32,
  border: "1px solid rgba(255,255,255,0.65)",
  background:
    "radial-gradient(circle at top right, rgba(59,130,246,0.06), transparent 22%), rgba(255,255,255,0.74)",
  backdropFilter: "blur(14px)",
  boxShadow: "0 20px 60px rgba(15,23,42,0.07)",
  padding: 18,
};

const glassPanel = {
  borderRadius: 32,
  border: "1px solid rgba(255,255,255,0.65)",
  background:
    "radial-gradient(circle at top right, rgba(124,58,237,0.06), transparent 24%), rgba(255,255,255,0.74)",
  backdropFilter: "blur(14px)",
  boxShadow: "0 20px 60px rgba(15,23,42,0.07)",
  padding: 18,
};

const stackCol = {
  display: "flex",
  flexDirection: "column",
  gap: 18,
};

const panelTitle = {
  fontSize: 18,
  fontWeight: 800,
  color: "#0f172a",
};

const panelSubtitle = {
  marginTop: 6,
  fontSize: 13,
  lineHeight: 1.6,
  color: "#64748b",
};

const miniStatsGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 12,
};

const miniStatCard = {
  borderRadius: 22,
  border: "1px solid rgba(226,232,240,0.92)",
  background: "rgba(248,250,252,0.90)",
  padding: 14,
};

const miniStatTitle = {
  fontSize: 12,
  fontWeight: 700,
  color: "#64748b",
};

const miniStatValue = {
  marginTop: 10,
  fontSize: 34,
  lineHeight: 1,
  fontWeight: 800,
  color: "#0f172a",
};

const miniStatNote = {
  marginTop: 8,
  fontSize: 12,
  color: "#64748b",
};

const divider = {
  height: 1,
  background: "rgba(226,232,240,0.95)",
  margin: "18px 0",
};

const summaryRows = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

const summaryRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
};

const summaryLabel = {
  fontSize: 13,
  fontWeight: 600,
  color: "#475569",
};

const summaryValue = {
  fontSize: 13,
  fontWeight: 800,
  color: "#0f172a",
};

const statusPill = {
  padding: "7px 12px",
  borderRadius: 999,
  background: "linear-gradient(180deg,#eef2ff 0%,#e0e7ff 100%)",
  color: "#4338ca",
  fontSize: 12,
  fontWeight: 800,
};

const actionItem = {
  display: "flex",
  gap: 10,
  alignItems: "flex-start",
  padding: "12px 0",
  borderBottom: "1px solid rgba(226,232,240,0.92)",
};

const actionDot = {
  width: 8,
  height: 8,
  borderRadius: 999,
  background: "#2563eb",
  marginTop: 7,
  flexShrink: 0,
};

const actionTitle = {
  fontSize: 15,
  fontWeight: 800,
  color: "#0f172a",
};

const actionText = {
  marginTop: 4,
  fontSize: 13,
  lineHeight: 1.55,
  color: "#64748b",
};

const btnPrimary = {
  background: "linear-gradient(135deg,#3b82f6 0%,#2563eb 55%,#1d4ed8 100%)",
  color: "#fff",
  border: "none",
  borderRadius: 18,
  padding: "12px 18px",
  fontWeight: 800,
  fontSize: 14,
  cursor: "pointer",
  boxShadow: "0 14px 26px rgba(37,99,235,0.24)",
};

const btnSoft = {
  background: "rgba(255,255,255,0.8)",
  color: "#1d4ed8",
  border: "1px solid rgba(59,130,246,0.14)",
  borderRadius: 18,
  padding: "12px 18px",
  fontWeight: 800,
  fontSize: 14,
  cursor: "pointer",
};

const btnGhost = {
  background: "transparent",
  color: "#0f172a",
  border: "1px solid rgba(15,23,42,0.12)",
  borderRadius: 12,
  padding: "10px 14px",
  fontWeight: 700,
  cursor: "pointer",
};

const errorBanner = {
  padding: 14,
  borderRadius: 18,
  background: "rgba(254,242,242,0.95)",
  border: "1px solid #fecaca",
  color: "#991b1b",
  fontWeight: 600,
};

const centerState = {
  padding: 20,
};

const centerCard = {
  maxWidth: 540,
  borderRadius: 28,
  border: "1px solid rgba(255,255,255,0.65)",
  background: "rgba(255,255,255,0.74)",
  backdropFilter: "blur(14px)",
  boxShadow: "0 20px 60px rgba(15,23,42,0.07)",
  padding: 20,
};

const mutedText = {
  marginTop: 8,
  color: "#64748b",
  lineHeight: 1.6,
};